
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface TravelNewsItem {
  id: string;
  title: string;
  description: string | null;
  url: string;
  image_url: string | null;
  source: string;
  category: string | null;
  published_at: string;
  created_at: string;
}

interface UseTravelNewsOptions {
  limit?: number;
  page?: number;
  filter?: string;
  cacheDuration?: number; // In milliseconds
  maxPages?: number; // New option to limit the maximum number of pages
}

interface UseTravelNewsResult {
  news: TravelNewsItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  totalCount: number;
  totalPages: number;
}

// Sources to exclude if needed
const EXCLUDED_SOURCES: string[] = [
  'digital journal',
  'quartz india',
  'quartz',
  'the times of india',
  'techcrunch',
  'wired'
];

// Function to check if a source should be excluded
const isExcludedSource = (source: string): boolean => {
  if (!source) return false;
  return EXCLUDED_SOURCES.some(excluded => 
    source.toLowerCase().includes(excluded.toLowerCase())
  );
};

// Cache for previously fetched news
const newsCache = {
  data: null as TravelNewsItem[] | null,
  count: 0,
  timestamp: 0,
  query: '',
};

export function useTravelNews(options: UseTravelNewsOptions = {}): UseTravelNewsResult {
  const { 
    limit = 12, 
    page = 1, 
    filter = '', 
    cacheDuration = 5 * 60 * 1000, // 5 minutes default cache
    maxPages = 3 // Default max pages is 3
  } = options;
  const [news, setNews] = useState<TravelNewsItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  
  // Create a cache key based on filter
  const cacheKey = filter;
  const isCacheValid = () => {
    return (
      newsCache.data !== null &&
      newsCache.query === cacheKey &&
      Date.now() - newsCache.timestamp < cacheDuration
    );
  };

  const fetchNews = useCallback(async (ignoreCache = false) => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if we can use cached data for the count
      if (isCacheValid() && !ignoreCache) {
        setTotalCount(newsCache.count);
        
        // Apply max pages limit to cached data
        const calculatedPages = Math.ceil(newsCache.count / limit);
        const limitedPages = Math.min(calculatedPages, maxPages);
        setTotalPages(limitedPages);
        
        // Apply pagination to the cached data
        const from = (page - 1) * limit;
        const to = Math.min(from + limit, newsCache.data!.length);
        
        // If we have enough cached data for this page, use it
        if (newsCache.data!.length >= to) {
          setNews(newsCache.data!.slice(from, to));
          setIsLoading(false);
          return;
        }
      }

      // First, get the total count for pagination
      const countQuery = supabase
        .from('travel_news')
        .select('id', { count: 'exact' });

      // Apply text search filter if provided
      if (filter) {
        countQuery.or(`title.ilike.%${filter}%,description.ilike.%${filter}%,source.ilike.%${filter}%,category.ilike.%${filter}%`);
      }

      const { count, error: countError } = await countQuery;

      if (countError) {
        throw new Error(countError.message);
      }

      setTotalCount(count || 0);
      
      // Calculate total pages but limit to maxPages
      const calculatedPages = Math.ceil((count || 0) / limit);
      const limitedPages = Math.min(calculatedPages, maxPages);
      setTotalPages(limitedPages);

      // Now fetch the actual data with pagination
      let query = supabase
        .from('travel_news')
        .select('*')
        .order('published_at', { ascending: false }); // Ensure we're getting the newest first

      // Apply text search filter if provided
      if (filter) {
        query = query.or(`title.ilike.%${filter}%,description.ilike.%${filter}%,source.ilike.%${filter}%,category.ilike.%${filter}%`);
      }

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error: dataError } = await query;

      if (dataError) {
        throw new Error(dataError.message);
      }

      // Filter out excluded sources if any
      const filteredData = data?.filter(article => !isExcludedSource(article.source)) || [];
      
      // Process any RSS-specific formatting issues
      const processedData = filteredData.map(article => ({
        ...article,
        // Ensure description is cleaned of any HTML tags that might come from RSS
        description: article.description 
          ? article.description.replace(/<\/?[^>]+(>|$)/g, "") 
          : null,
      }));
      
      setNews(processedData);
      
      // Update cache if we're on the first page and have a full dataset
      if (page === 1 && !ignoreCache) {
        newsCache.data = processedData;
        newsCache.count = count || 0;
        newsCache.timestamp = Date.now();
        newsCache.query = cacheKey;
      }
    } catch (err: any) {
      console.error('Error fetching travel news:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [limit, page, filter, cacheDuration, maxPages]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  return { 
    news, 
    isLoading, 
    error, 
    refetch: () => fetchNews(true), // Force ignore cache on manual refetch
    totalCount, 
    totalPages 
  };
}
